<section class="page">
  <header class="page-header">
    <div>
      <a routerLink="/transactions" class="back">← Transactions</a>
      <h1>Transaction detail</h1>
      <p>Full party routing, wallets, beneficiary and rent metadata</p>
    </div>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }

  @if (loading()) {
    <div class="panel muted">Loading…</div>
  } @else {
    @if (tx(); as t) {
      <div class="detail-grid">
        <div class="panel">
          <div class="panel-head">
            <h2>#{{ t.id }}</h2>
            <span class="badge" [attr.data-status]="t.status">{{ t.status || '—' }}</span>
          </div>
          <dl class="meta">
            <div><dt>Type</dt><dd>{{ t.type || '—' }}</dd></div>
            <div><dt>Leg</dt><dd>{{ t.leg || '—' }}</dd></div>
            <div><dt>Direction</dt><dd>{{ t.direction || '—' }}</dd></div>
            <div><dt>Amount</dt><dd>{{ t.amount }} {{ t.currency || 'AUD' }}</dd></div>
            <div><dt>Fee</dt><dd>{{ t.fee ?? 0 }}</dd></div>
            <div><dt>Payment type</dt><dd>{{ t.payment_type || '—' }}</dd></div>
            <div><dt>Reference</dt><dd>{{ t.reference || '—' }}</dd></div>
            <div><dt>Description</dt><dd>{{ t.description || '—' }}</dd></div>
            <div><dt>Failure</dt><dd>{{ t.failure_reason || '—' }}</dd></div>
            <div><dt>Kobble tx</dt><dd class="mono">{{ t.kobble_transaction_id || '—' }}</dd></div>
            <div>
              <dt>Rent payment</dt>
              <dd>
                @if (t.rent_payment_id) {
                  #{{ t.rent_payment_id }}
                } @else {
                  —
                }
              </dd>
            </div>
            <div><dt>Bank account ID</dt><dd>{{ t.bank_account_id ?? '—' }}</dd></div>
            <div><dt>Created</dt><dd>{{ t.created_at ? (t.created_at | date: 'medium') : '—' }}</dd></div>
            <div><dt>Completed</dt><dd>{{ t.completed_at ? (t.completed_at | date: 'medium') : '—' }}</dd></div>
          </dl>
        </div>

        <div class="panel">
          <h2>Sender / initiator</h2>
          <dl class="meta">
            <div>
              <dt>User</dt>
              <dd>
                @if (t.user_id) {
                  <a [routerLink]="['/users', t.user_id]">{{ userLabel(t) }}</a>
                } @else {
                  {{ userLabel(t) }}
                }
              </dd>
            </div>
            <div><dt>User ID</dt><dd>{{ t.user_id ?? '—' }}</dd></div>
            <div><dt>Initiated by</dt><dd>{{ t.initiated_by_user_id ?? '—' }}</dd></div>
            <div><dt>From wallet (local)</dt><dd>{{ t.from_wallet_id ?? t.wallet_id ?? '—' }}</dd></div>
            <div><dt>From Kobble wallet</dt><dd class="mono">{{ t.from_kobble_wallet_id || '—' }}</dd></div>
            <div><dt>Merchant wallet</dt><dd class="mono">{{ t.merchant_kobble_wallet_id || '—' }}</dd></div>
          </dl>
        </div>

        <div class="panel">
          <h2>Receiver / counterparty</h2>
          <dl class="meta">
            <div><dt>To party type</dt><dd>{{ t.to_party_type || '—' }}</dd></div>
            <div><dt>To user ID</dt><dd>{{ t.to_user_id ?? '—' }}</dd></div>
            <div><dt>To wallet (local)</dt><dd>{{ t.to_wallet_id ?? '—' }}</dd></div>
            <div><dt>To Kobble wallet</dt><dd class="mono">{{ t.to_kobble_wallet_id || '—' }}</dd></div>
            <div><dt>Beneficiary ID</dt><dd>{{ t.beneficiary_id ?? '—' }}</dd></div>
            <div><dt>Name</dt><dd>{{ t.counterparty_name || t.beneficiary?.name || '—' }}</dd></div>
            <div><dt>Account</dt><dd class="mono">{{ t.counterparty_account_number || t.beneficiary?.account_number || '—' }}</dd></div>
            <div><dt>BSB</dt><dd class="mono">{{ t.counterparty_bsb || t.beneficiary?.bsb || '—' }}</dd></div>
            <div><dt>Bank</dt><dd>{{ t.counterparty_bank_name || t.beneficiary?.bank_name || '—' }}</dd></div>
            <div><dt>PayID</dt><dd class="mono">{{ t.counterparty_pay_id || t.beneficiary?.pay_id || '—' }}</dd></div>
            <div><dt>PayID type</dt><dd>{{ t.counterparty_pay_id_type || t.beneficiary?.pay_id_type || '—' }}</dd></div>
            <div>
              <dt>Kobble beneficiary</dt>
              <dd class="mono">{{ t.beneficiary?.kobble_beneficiary_id || '—' }}</dd>
            </div>
          </dl>
        </div>

        @if (t.wallet; as w) {
          <div class="panel">
            <h2>Linked wallet</h2>
            <dl class="meta">
              <div><dt>Local ID</dt><dd>{{ w.id }}</dd></div>
              <div><dt>Kobble wallet</dt><dd class="mono">{{ w.kobble_wallet_id || '—' }}</dd></div>
              <div><dt>Account</dt><dd class="mono">{{ w.account_number || '—' }}</dd></div>
              <div><dt>BSB / code</dt><dd class="mono">{{ w.bank_code || '—' }}</dd></div>
              <div><dt>Balance</dt><dd>{{ w.balance ?? 0 }} {{ w.currency || 'AUD' }}</dd></div>
              <div><dt>Available</dt><dd>{{ w.available_balance ?? 0 }}</dd></div>
              <div><dt>Reserved</dt><dd>{{ w.reserved_balance ?? 0 }}</dd></div>
              <div><dt>Product</dt><dd>{{ w.partner_product || '—' }}</dd></div>
              <div><dt>Status</dt><dd>{{ w.status || '—' }}</dd></div>
            </dl>
          </div>
        }

        @if (t.beneficiary; as b) {
          <div class="panel">
            <h2>Beneficiary record</h2>
            <dl class="meta">
              <div><dt>ID</dt><dd>{{ b.id }}</dd></div>
              <div><dt>Kobble ID</dt><dd class="mono">{{ b.kobble_beneficiary_id || '—' }}</dd></div>
              <div><dt>Name</dt><dd>{{ b.name || '—' }}</dd></div>
              <div><dt>Nickname</dt><dd>{{ b.nickname || '—' }}</dd></div>
              <div><dt>Account</dt><dd class="mono">{{ b.account_number || b.iban || '—' }}</dd></div>
              <div><dt>BSB / code</dt><dd class="mono">{{ b.bsb || b.bank_code || b.sort_code || '—' }}</dd></div>
              <div><dt>Bank</dt><dd>{{ b.bank_name || '—' }}</dd></div>
              <div><dt>Country</dt><dd>{{ b.bank_country || '—' }}</dd></div>
              <div><dt>PayID</dt><dd class="mono">{{ b.pay_id || '—' }}</dd></div>
              <div><dt>PayID type</dt><dd>{{ b.pay_id_type || '—' }}</dd></div>
              <div><dt>Landlord</dt><dd>{{ b.is_landlord ? 'yes' : 'no' }}</dd></div>
              <div><dt>Status</dt><dd>{{ b.status || '—' }}</dd></div>
              <div><dt>Currency</dt><dd>{{ b.currency || '—' }}</dd></div>
            </dl>
          </div>
        }

        @if (t.bank_account; as ba) {
          <div class="panel">
            <h2>User bank account (funding ref)</h2>
            <dl class="meta">
              <div><dt>ID</dt><dd>{{ ba.id }}</dd></div>
              <div><dt>Kobble account</dt><dd class="mono">{{ ba.kobble_account_id || '—' }}</dd></div>
              <div><dt>Name</dt><dd>{{ ba.account_name || '—' }}</dd></div>
              <div><dt>Number</dt><dd class="mono">{{ ba.account_number || '—' }}</dd></div>
              <div><dt>BSB</dt><dd class="mono">{{ ba.bsb || '—' }}</dd></div>
              <div><dt>Status</dt><dd>{{ ba.status || '—' }}</dd></div>
            </dl>
          </div>
        }

        @if (t.rent_payment; as rp) {
          <div class="panel">
            <h2>Linked rent payment</h2>
            <dl class="meta">
              <div><dt>ID</dt><dd>{{ rp.id }}</dd></div>
              <div><dt>Rent amount</dt><dd>{{ rp.rent_amount }} {{ rp.currency || '' }}</dd></div>
              <div><dt>Bank charged</dt><dd>{{ rp.bank_amount_charged ?? '—' }}</dd></div>
              <div><dt>Status</dt><dd>{{ rp.status || '—' }}</dd></div>
              <div><dt>Landlord</dt><dd>{{ rp.landlord_name || '—' }}</dd></div>
              <div><dt>Property</dt><dd>{{ rp.property_address || '—' }}</dd></div>
              <div><dt>Reference</dt><dd>{{ rp.reference || '—' }}</dd></div>
            </dl>
          </div>
        }
      </div>
    }
  }
</section>
